import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CCard, CCardBody, CCardHeader, CButton, CRow, CCol, CAlert, CSpinner,
  CForm, CFormLabel, CFormInput, CFormTextarea,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilTrash, cilPlus, cilArrowLeft, cilPencil, cilCheck, cilX } from '@coreui/icons';
import api from '../../services/api';

export default function MarketingTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingEditId, setSavingEditId] = useState(null);

  function load() {
    setError('');
    api.get('/marketing/templates')
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => setError('Failed to load templates.'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/marketing/templates', { title: title.trim(), body: body.trim() });
      setTitle('');
      setBody('');
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(t) {
    setEditId(t.id);
    setEditTitle(t.title);
    setEditBody(t.body);
  }

  function cancelEdit() {
    setEditId(null);
    setEditTitle('');
    setEditBody('');
  }

  async function handleSaveEdit(id) {
    if (!editTitle.trim() || !editBody.trim()) return;
    setSavingEditId(id);
    setError('');
    try {
      await api.put(`/marketing/templates/${id}`, { title: editTitle.trim(), body: editBody.trim() });
      setEditId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Save failed.');
    } finally {
      setSavingEditId(null);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this template?')) return;
    setDeletingId(id);
    setError('');
    try {
      await api.delete(`/marketing/templates/${id}`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Marketing — Message templates</h5>
          <div className="text-muted small mt-1">
            Optional placeholders: <code>{'{{nama}}'}</code>, <code>{'{{telhp}}'}</code>.{' '}
            <Link to="/marketing/send">Back to send queue</Link>
          </div>
        </CCol>
        <CCol xs="auto">
          <CButton color="secondary" variant="outline" size="sm" component={Link} to="/marketing/send">
            <CIcon icon={cilArrowLeft} className="me-1" /> Send queue
          </CButton>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}

      <CCard className="mb-3 border-0 shadow-sm">
        <CCardHeader className="bg-white">New template</CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleAdd}>
            <CRow className="g-3">
              <CCol md={4}>
                <CFormLabel>Title</CFormLabel>
                <CFormInput value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="e.g. Holiday promo" />
              </CCol>
              <CCol md={8}>
                <CFormLabel>Body</CFormLabel>
                <CFormTextarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Hi {{nama}}, …" />
              </CCol>
            </CRow>
            <CButton type="submit" color="success" className="mt-3" disabled={saving || !title.trim() || !body.trim()}>
              <CIcon icon={cilPlus} className="me-1" /> {saving ? 'Saving…' : 'Add template'}
            </CButton>
          </CForm>
        </CCardBody>
      </CCard>

      <CCard className="border-0 shadow-sm">
        <CCardHeader className="bg-white">Saved templates</CCardHeader>
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="success" /></div>
          ) : templates.length === 0 ? (
            <div className="p-4 text-muted">No templates yet.</div>
          ) : (
            <CTable responsive hover className="mb-0 small">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Title</CTableHeaderCell>
                  <CTableHeaderCell>Preview</CTableHeaderCell>
                  <CTableHeaderCell>By</CTableHeaderCell>
                  <CTableHeaderCell className="text-end"> </CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {templates.map((t) => {
                  const isEditing = editId === t.id;
                  if (isEditing) {
                    return (
                      <CTableRow key={t.id} className="align-top">
                        <CTableDataCell>
                          <CFormInput
                            size="sm"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            maxLength={120}
                          />
                        </CTableDataCell>
                        <CTableDataCell colSpan={2}>
                          <CFormTextarea
                            rows={4}
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            style={{ fontSize: '0.85rem' }}
                          />
                        </CTableDataCell>
                        <CTableDataCell className="text-end text-nowrap">
                          <CButton
                            size="sm"
                            color="success"
                            variant="ghost"
                            className="me-1"
                            disabled={savingEditId === t.id || !editTitle.trim() || !editBody.trim()}
                            onClick={() => handleSaveEdit(t.id)}
                          >
                            {savingEditId === t.id ? <CSpinner size="sm" /> : <CIcon icon={cilCheck} />}
                          </CButton>
                          <CButton size="sm" color="secondary" variant="ghost" onClick={cancelEdit}>
                            <CIcon icon={cilX} />
                          </CButton>
                        </CTableDataCell>
                      </CTableRow>
                    );
                  }
                  return (
                    <CTableRow key={t.id}>
                      <CTableDataCell className="fw-semibold">{t.title}</CTableDataCell>
                      <CTableDataCell style={{ maxWidth: 360, whiteSpace: 'pre-wrap' }} className="text-break">
                        {(t.body || '').slice(0, 200)}{(t.body || '').length > 200 ? '…' : ''}
                      </CTableDataCell>
                      <CTableDataCell>{t.created_by}</CTableDataCell>
                      <CTableDataCell className="text-end text-nowrap">
                        <CButton
                          size="sm"
                          color="secondary"
                          variant="ghost"
                          className="me-1"
                          onClick={() => startEdit(t)}
                        >
                          <CIcon icon={cilPencil} />
                        </CButton>
                        <CButton
                          size="sm"
                          color="danger"
                          variant="ghost"
                          disabled={deletingId === t.id}
                          onClick={() => handleDelete(t.id)}
                        >
                          {deletingId === t.id ? <CSpinner size="sm" /> : <CIcon icon={cilTrash} />}
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}
              </CTableBody>
            </CTable>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
